import React from 'react';
import Link from './Link';
import LineChart from './LineChart';
import { ReactComponent as ArrowLeft } from 'assets/img/arrow-left.svg';
import { useEffect, useState } from 'react';
import ChartLegend from './ChartLegend';

export default function CountySidePanel({
  children,
  socioeconomicYearsData = null,
  features = null,
  socioeconomicFactor = null,
  backLinkAction,
  activeCounty,
}) {
  const [dataChart1, setDataChart1] = useState(null);
  const [dataChart2, setDataChart2] = useState(null);
  const [previousActiveCounty, setPreviousActiveCounty] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);

  let socioeconomicFactorLabel = findFeatureLabel(features, socioeconomicFactor);

  useEffect(() => {
    if (!socioeconomicYearsData) {
      return;
    }

    if (activeCounty?.countyname === previousActiveCounty?.countyname) {
      return;
    }

    let broadbandYearsChartData = socioeconomicYearsData.map((item) => item.broadband);
    let socioeconomicYearsChartLabels = socioeconomicYearsData.map((item) => item.year);

    setDataChart1({
      labels: socioeconomicYearsChartLabels,
      datasets: [
        {
          data: broadbandYearsChartData,
          borderColor: function (context) {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              // This case happens on initial chart load
              return;
            }

            return getGradientBlue(ctx, chartArea);
          },
          fill: false,
          tension: 0.5,
        },
      ],
    });

    setPreviousActiveCounty(activeCounty);
  }, [activeCounty, previousActiveCounty?.countyname, socioeconomicYearsData]);

  useEffect(() => {
    if (!socioeconomicYearsData || !activeCounty?.countyname) {
      return;
    }

    if (
      socioeconomicFactor === previousSocioeconomicFactor &&
      activeCounty?.countyname === previousActiveCounty?.countyname
    ) {
      return;
    }

    if (socioeconomicYearsData?.[0][socioeconomicFactor] === undefined) {
      return;
    }

    let socioeconomicYearsChartData = socioeconomicYearsData.map(
      (item) => item[socioeconomicFactor]
    );
    let socioeconomicYearsChartLabels = socioeconomicYearsData.map((item) => item.year);

    setDataChart2({
      labels: socioeconomicYearsChartLabels,
      datasets: [
        {
          data: socioeconomicYearsChartData,
          borderColor: function (context) {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              // This case happens on initial chart load
              return;
            }

            return getGradientBlue(ctx, chartArea);
          },
          fill: false,
          tension: 0.5,
        },
      ],
    });

    setPreviousActiveCounty(activeCounty);
    setPreviousSocioeconomicFactor(socioeconomicFactor);
  }, [
    activeCounty,
    previousActiveCounty?.countyname,
    previousSocioeconomicFactor,
    socioeconomicFactor,
    socioeconomicYearsData,
  ]);

  let getGradientBlue = function (ctx, chartArea) {
    let gradient;

    gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    gradient.addColorStop(0, '#F3F5F7');
    gradient.addColorStop(0.1, '#D6DFF4');
    gradient.addColorStop(0.5, '#142C60');
    gradient.addColorStop(1, '#142C60');

    return gradient;
  };

  return (
    <div data-component-name='CountySidePanel' className='h-full'>
      <div className='flex flex-col h-full text-blue-600'>
        {backLinkAction && (
          <Link
            action={backLinkAction}
            icon={<ArrowLeft />}
            isIconLeft={true}
            color='text-blue-500/60'
          >
            <span className='text-base -translate-y-px'>back</span>
          </Link>
        )}

        <h2 className='pb-4 mt-4 text-lg font-bold'>Visualizations</h2>

        <div className='h-full overflow-auto pb-7'>
          <h3 className='mt-3 text-sm font-semibold'>Broadband score over the years</h3>
          <div className='mt-3 '>
            <LineChart
              data={dataChart1}
              labelY={<span>Broadband score</span>}
              labelX={<span className='pl-3'>Years</span>}
            >
              <ChartLegend items={[{ value: 'blue', label: 'Observation' }]} />
            </LineChart>
          </div>

          <h3 className='text-sm font-semibold mt-7'>
            {socioeconomicFactorLabel} over the years
          </h3>
          <div className='mt-3 '>
            <LineChart
              data={dataChart2}
              labelY={<span>{socioeconomicFactorLabel}</span>}
              labelX={<span className='pl-3'>Years</span>}
            >
              <ChartLegend items={[{ value: 'blue', label: 'Observation' }]} />
            </LineChart>
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}

const findFeatureLabel = function (features, input) {
  if (features === null) {
    return;
  }

  const categoryMatch =
    features.find((categoryData) => categoryData.category === input) || null;

  if (categoryMatch !== null) {
    return categoryMatch.category;
  }

  const featureMatch = features
    .flatMap((categoryData) => categoryData.features)
    .find((featureData) => featureData.id === input);

  return featureMatch ? featureMatch.label : null;
};
